import { hardRedirectTo } from '../../../utils';
import OrgSelectForm from '../components/OrgSelectForm';
import { socialLoginEntryUrl, withNext } from '../ssoEntry';
import { STRATEGY_LABEL } from '../strategyLabels';
import type { Candidate, SignupSuggestion } from '../../../../shared/types/routes/auth/v2';
import type { OrgOption } from '../components/OrgSelectForm';

// Sentinel option id for the signup "create a new organization" choice. Real organization ids are always positive,
// so this never collides with a candidate or suggestion.
export const CREATE_NEW_ORG_ID = 0;

// Sentinel option id for the login "continue with your existing account" choice (an org-less orphan account). Negative,
// so it never collides with a real organization id or CREATE_NEW_ORG_ID.
export const CONTINUE_ORPHAN_ID = -1;

export type OrgPickerStepProps = {
  candidates: Candidate[];
  signupSuggestions: SignupSuggestion[];
  next?: string;
  onPickCandidate: (org: Candidate) => void;
  onBack: () => void;
  /**
   * Signup mode: when provided, the picker appends a "Create a new organization" option and uses signup copy.
   * Login callers omit it. Picking it calls this instead of resolving a candidate/suggestion.
   */
  onCreateNew?: () => void;
  /**
   * Login mode: when provided (the identifier resolved to an org-less orphan account whose email is on a claimed
   * domain), the picker appends a "Continue with your existing account" option beside the domain suggestions. Picking
   * it calls this — the caller routes into the org-less credential/social step rather than a suggested org.
   */
  onContinueOrphan?: () => void;
};

const pickerLegend = (isSignup: boolean, hasCandidates: boolean): string => {
  if (isSignup) {
    return hasCandidates
      ? 'This email already has an account. Sign in to an organization, or create a new one:'
      : 'Your email domain is managed by an organization. Sign in to it, or create a new one:';
  }
  return hasCandidates
    ? 'You have access to multiple organizations. Choose one to continue:'
    : 'Sign in to an organization that manages your email domain:';
};

const ssoEntryUrl = (suggestion: SignupSuggestion, next?: string): string | null => {
  switch (suggestion.ssoMethod) {
    case 'saml':
      // Prefer the backend-supplied entry URL; the literal stays only as a legacy-shape fallback.
      return withNext(suggestion.samlEntryUrl ?? `/account/saml/${suggestion.organizationId}`, next);
    case 'google':
      return socialLoginEntryUrl('google', next);
    case 'ldap':
      // LDAP has no redirect IdP entry; its pick advances to the credential form instead (see handleSubmit).
      return null;
    default: {
      const exhaustiveCheck: never = suggestion.ssoMethod;
      return exhaustiveCheck;
    }
  }
};

/**
 * A domain-claim LDAP org the user has not joined yet: synthesize the candidate its credential form needs, so a
 * non-member reaches the LDAP form and is provisioned on first successful bind. `availableMethods` is LDAP-only —
 * a non-member can't password-login, and the credentials route resolves the real strategy from the org's config.
 */
const ldapSuggestionCandidate = (suggestion: SignupSuggestion): Candidate => ({
  organizationId: suggestion.organizationId,
  organizationName: suggestion.organizationName,
  availableMethods: [{ type: 'ldap' }],
  requiresSso: true,
  allowedStrategies: ['ldap'],
});

const suggestionToOption = (suggestion: SignupSuggestion): OrgOption => ({
  organizationId: suggestion.organizationId,
  label: suggestion.organizationName,
  secondaryLabel: `Sign in with ${STRATEGY_LABEL[suggestion.ssoMethod]}`,
});

/**
 * The organization-picker step. When a resolved identifier maps to more than one member org — or, when the user has no
 * member org, to domain-claim orgs they can join — this renders a radio group so the user chooses which org to
 * authenticate into. Member-org candidates take priority; signup suggestions show only when there are no candidates.
 *
 * The picker UI itself lives in the shared {@link OrgSelectForm}; this component owns the credential-specific data
 * shaping and what a pick does: advance a member candidate (or a domain-claim LDAP org) to its credential form;
 * redirect a redirect-based (Google/SAML) suggestion to its SSO entry point.
 */
const OrgPickerStep = (
  { candidates, signupSuggestions, next, onPickCandidate, onBack, onCreateNew, onContinueOrphan }: OrgPickerStepProps,
) => {
  const hasCandidates = candidates.length > 0;
  const options: OrgOption[] = [
    ...(hasCandidates
      ? candidates.map(candidate => ({ organizationId: candidate.organizationId, label: candidate.organizationName }))
      : signupSuggestions.map(suggestionToOption)),
    ...(onCreateNew ? [{ organizationId: CREATE_NEW_ORG_ID, label: 'Create a new organization' }] : []),
    ...(onContinueOrphan ? [{ organizationId: CONTINUE_ORPHAN_ID, label: 'Continue with your existing account' }] : []),
  ];

  const legend = pickerLegend(Boolean(onCreateNew), hasCandidates);

  const handleSubmit = (organizationId: number) => {
    if (onCreateNew && organizationId === CREATE_NEW_ORG_ID) {
      onCreateNew();
      return;
    }
    if (onContinueOrphan && organizationId === CONTINUE_ORPHAN_ID) {
      onContinueOrphan();
      return;
    }
    const candidate = candidates.find(c => c.organizationId === organizationId);
    if (candidate) {
      onPickCandidate(candidate);
      return;
    }
    const suggestion = signupSuggestions.find(s => s.organizationId === organizationId);
    if (!suggestion) {
      return;
    }
    if (suggestion.ssoMethod === 'ldap') {
      // Advance to the org's LDAP credential form; the credential POST provisions a first-time user on bind.
      onPickCandidate(ldapSuggestionCandidate(suggestion));
      return;
    }
    const ssoUrl = ssoEntryUrl(suggestion, next);
    if (ssoUrl) {
      hardRedirectTo(ssoUrl);
    }
  };

  return <OrgSelectForm legend={legend} options={options} onSubmit={handleSubmit} onBack={onBack} />;
};

export default OrgPickerStep;
